// RO + 2 check digits + 4-letter bank code + 16 alphanumeric = 24 chars total.
const ROMANIAN_IBAN_FORMAT = /^RO\d{2}[A-Z]{4}[A-Z0-9]{16}$/;
const IBAN_CHECKSUM_MOD97_BLOCK_SIZE = 9;
const VALID_IBAN_CHECKSUM_REMAINDER = 1;

/**
 * Validates a Romanian IBAN: checks the structural format (RO + 24 chars)
 * and the ISO 7064 mod-97 checksum, so well-formatted but bogus IBANs are
 * rejected too, not just wrong-length strings. Mirrors the backend's
 * IsRomanianIban validator so client and server agree on what's valid.
 */
export function isValidRomanianIban(value: string): boolean {
  if (typeof value !== "string") {
    return false;
  }

  const normalizedIban = value.replace(/\s+/g, "").toUpperCase();

  return (
    ROMANIAN_IBAN_FORMAT.test(normalizedIban) &&
    ibanChecksumIsValid(normalizedIban)
  );
}

/**
 * ISO 7064 mod-97-10 checksum: move the first 4 chars to the end, convert
 * letters to numbers (A=10...Z=35), then reduce mod 97 in 9-digit blocks
 * (JS-safe integer size) until a single remainder is left. Valid IBANs
 * always leave remainder 1.
 */
function ibanChecksumIsValid(normalizedIban: string): boolean {
  const rearrangedIban = normalizedIban.slice(4) + normalizedIban.slice(0, 4);
  const numericIban = rearrangedIban
    .split("")
    .map((character) =>
      /[0-9]/.test(character)
        ? character
        : (character.charCodeAt(0) - 55).toString()
    )
    .join("");

  let remainder = numericIban;
  while (remainder.length > IBAN_CHECKSUM_MOD97_BLOCK_SIZE) {
    const block = remainder.slice(0, IBAN_CHECKSUM_MOD97_BLOCK_SIZE);
    remainder =
      (parseInt(block, 10) % 97).toString() +
      remainder.slice(IBAN_CHECKSUM_MOD97_BLOCK_SIZE);
  }

  return parseInt(remainder, 10) % 97 === VALID_IBAN_CHECKSUM_REMAINDER;
}
